import * as z from "zod";
import { userFacingError } from "../authorization";
import { copyAllCommitFiles } from "../commitFiles";
import { zql } from "../generatedDrizzleSchema";
import { actorColumns, creatorColumns } from "../rbac/roles";
import { assertAttachmentsSupported } from "./attachmentValidation";
import { mutator } from "./mutator";

const attachmentSchema = z.object({
  id: z.string().regex(/^[a-zA-Z0-9_-]+$/),
  fileName: z.string(),
  mediaType: z.string(),
  sizeBytes: z.number(),
  data: z.string(),
});

export const createBranchSchema = z.object({
  branchId: z.string(),
  commitId: z.string(),
  chatId: z.string(),
  workflowId: z.string(),
  name: z.string(),
  userEntryId: z.string().optional(),
  prompt: z.string().optional(),
  isDictated: z.boolean().optional(),
  planMode: z.boolean().optional(),
  attachments: z.array(attachmentSchema).optional(),
  previousBranchId: z.string().optional(),
  sourceCommitId: z.string().optional(),
});

export const createBranch = mutator({
  schema: createBranchSchema,
  resolve: {
    workflow: { kind: "workflow", from: "workflowId" },
    previousBranch: {
      kind: "branch",
      from: "previousBranchId",
      optional: true,
    },
    sourceCommit: { kind: "commit", from: "sourceCommitId", optional: true },
  },
  permissions: [
    {
      permission: "space:write",
      scopeId: ({ args }) => args.workflow.spaceId,
    },
  ],
  fn: async ({
    tx,
    ctx,
    args: {
      branchId,
      commitId,
      chatId,
      workflow,
      name,
      userEntryId,
      prompt,
      isDictated,
      planMode,
      attachments,
      previousBranch,
      sourceCommit,
    },
  }) => {
    const creator = creatorColumns(ctx);
    const actor = actorColumns(ctx);
    if (name === "main") {
      throw userFacingError("Cannot create a branch named “main”");
    }
    await assertAttachmentsSupported(tx, attachments);

    const now = Date.now();
    const hasPrompt = !!(userEntryId && prompt);
    const lastChatEntryAt = hasPrompt ? now : null;

    const [mainBranch] = await tx.run(
      zql.workflowBranches
        .where("workflowId", workflow.id)
        .where("name", "main")
    );

    let sourceCommitId: string;
    let forkCommitId: string | null;

    if (sourceCommit) {
      if (sourceCommit.branch.workflowId !== workflow.id) {
        throw userFacingError("Source commit not found");
      }
      sourceCommitId = sourceCommit.id;
      forkCommitId = mainBranch?.headCommitId ?? null;
    } else {
      if (!mainBranch?.headCommitId) {
        throw userFacingError("Main branch not found");
      }
      sourceCommitId = mainBranch.headCommitId;
      forkCommitId = mainBranch.headCommitId;
      if (previousBranch) {
        if (previousBranch.workflowId !== workflow.id) {
          throw userFacingError("Previous branch not found");
        }
        if (previousBranch.headCommitId) {
          sourceCommitId = previousBranch.headCommitId;
          forkCommitId = previousBranch.headCommitId;
        }
      }
    }

    await tx.mutate.workflowBranches.insert({
      id: branchId,
      workflowId: workflow.id,
      userId: actor.userId,
      serviceAccountId: actor.serviceAccountId,
      name,
      headCommitId: commitId,
      forkCommitId,
      createdAt: now,
      lastChatEntryAt,
      lastChatEntryType: hasPrompt ? "UserMessage" : null,
      respondingAt: prompt ? now : null,
      tenantId: ctx.tenantId,
    });

    if (lastChatEntryAt) {
      await tx.mutate.workflows.update({
        id: workflow.id,
        updatedAt: now,
      });
    }

    // Create initial checkpoint for the branch
    await tx.mutate.workflowCommits.insert({
      id: commitId,
      branchId,
      parentCommitId: sourceCommitId,
      chatId,
      message: "Starting point",
      createdAt: now,
      createdByUserId: creator.createdByUserId,
      createdByServiceAccountId: creator.createdByServiceAccountId,
      tenantId: ctx.tenantId,
    });

    // Copy all files from source to the new commit
    await copyAllCommitFiles(
      { tx, commitId: sourceCommitId, tenantId: ctx.tenantId },
      commitId
    );

    await tx.mutate.chats.insert({
      id: chatId,
      commitId,
      createdAt: now,
      lastChatEntryType: hasPrompt ? "UserMessage" : null,
      respondingAt: prompt ? now : null,
      tenantId: ctx.tenantId,
    });

    if (userEntryId && prompt) {
      await tx.mutate.chatEntries.insert({
        id: userEntryId,
        chatId,
        entryType: "UserMessage",
        createdAt: now,
        previousEntryId: null,
        tenantId: ctx.tenantId,
      });
      await tx.mutate.userMessages.insert({
        entryId: userEntryId,
        userId: actor.userId,
        serviceAccountId: actor.serviceAccountId,
        text: prompt,
        isDictated: isDictated ?? null,
        planMode: planMode ?? null,
        tenantId: ctx.tenantId,
      });
      // Contributors are the human facepile; a pure service account records none.
      if (actor.userId) {
        await tx.mutate.branchContributors.insert({
          branchId,
          workflowId: workflow.id,
          userId: actor.userId,
          lastMessagedAt: now,
          tenantId: ctx.tenantId,
        });
      }
      for (const att of attachments ?? []) {
        await tx.mutate.attachments.insert({
          id: att.id,
          entryId: userEntryId,
          fileName: att.fileName,
          mediaType: att.mediaType,
          sizeBytes: att.sizeBytes,
          tenantId: ctx.tenantId,
        });
      }
    }
  },
});
